import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { RESONANCE_URL } from '../../../config/links';

/* =====================================================================
   Tokens — match the HTML mockup exactly.
   Kept local to this file so the rest of the app keeps its own palette.
   ===================================================================== */
const T = {
  bg: '#0B1012',
  bgPanel: '#0F1518',
  textPrimary: '#F8FAFC',
  textSecondary: 'rgba(248, 250, 252, 0.72)',
  textTertiary: 'rgba(248, 250, 252, 0.5)',
  textMuted: 'rgba(248, 250, 252, 0.35)',
  border: 'rgba(248, 250, 252, 0.08)',
  borderStrong: 'rgba(248, 250, 252, 0.14)',
  teal: '#10B981',
  tealBright: '#34D399',
  purple: '#A78BFA',
  orange: '#F97316',
};

const fInter = "'Inter', system-ui, -apple-system, sans-serif";
const fMono = "'JetBrains Mono', ui-monospace, monospace";
const fSerif = "'Instrument Serif', Georgia, serif";

/* ---- Section ---- */
const Section = styled(Box)({
  position: 'relative',
  padding: '0 0 120px',
  background: T.bg,
  color: T.textPrimary,
  fontFamily: fInter,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `radial-gradient(circle at 15% 30%, rgba(16, 185, 129, 0.04) 0%, transparent 45%),
      radial-gradient(circle at 85% 70%, rgba(167, 139, 250, 0.03) 0%, transparent 45%)`,
    pointerEvents: 'none',
  },
});

const Inner = styled(Box)({
  maxWidth: 1400,
  margin: '0 auto',
  padding: '0 48px',
  position: 'relative',
  '@media (max-width: 700px)': {
    padding: '0 24px',
  },
});

const ScrollArrow = styled(Box)({
  textAlign: 'center',
  padding: '32px 0 16px',
  color: T.textMuted,
  fontSize: 18,
});

/* ---- Section header ---- */
const SectHeader = styled(Box)({
  maxWidth: 860,
  margin: '0 auto 96px',
  textAlign: 'center',
  paddingTop: 80,
});

const SectEyebrow = styled(Box)({
  fontFamily: fMono,
  fontSize: 11,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: T.tealBright,
  marginBottom: 24,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 14,
  '&::before, &::after': {
    content: '""',
    width: 28,
    height: 1,
    background: T.tealBright,
    opacity: 0.5,
  },
});

const SectTitle = styled(Typography)({
  fontSize: 32,
  fontWeight: 500,
  letterSpacing: '-0.02em',
  lineHeight: 1.25,
  color: T.textPrimary,
  fontFamily: fInter,
  '& em': {
    fontFamily: fSerif,
    fontStyle: 'italic',
    fontWeight: 400,
    color: T.tealBright,
    fontSize: '1.1em',
  },
});

/* ---- Grid ---- */
const Cols = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 1,
  background: T.border,
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
  },
});

const Col = styled(Box)({
  background: T.bg,
  padding: '48px 40px 40px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 620,
  position: 'relative',
  '@media (max-width: 1200px)': {
    padding: '40px 32px 32px',
  },
});

const Visual = styled(Box)({
  height: 220,
  margin: '-48px -40px 40px',
  position: 'relative',
  overflow: 'hidden',
  borderBottom: `1px solid ${T.border}`,
  '@media (max-width: 1200px)': {
    margin: '-40px -32px 32px',
  },
});

const ColEyebrow = styled(Box)({
  fontFamily: fMono,
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  marginBottom: 20,
});

const ColHeadline = styled(Typography)({
  fontSize: 28,
  fontWeight: 600,
  letterSpacing: '-0.02em',
  lineHeight: 1.2,
  marginBottom: 20,
  color: T.textPrimary,
  fontFamily: fInter,
});

const ColBody = styled(Typography)({
  fontSize: 15,
  lineHeight: 1.65,
  color: T.textSecondary,
  marginBottom: 32,
  fontFamily: fInter,
});

const ColBodyWrap = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

/* ---- CTAs ---- */
const CtaButton = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 15,
  fontWeight: 600,
  color: '#FFF',
  textDecoration: 'none',
  padding: '14px 22px',
  background: T.orange,
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  alignSelf: 'flex-start',
  fontFamily: fInter,
  '&:hover': {
    background: '#EA580C',
    transform: 'translateY(-1px)',
    '& .arrow': { transform: 'translateX(3px)' },
  },
  '& .arrow': { transition: 'transform 0.2s ease' },
});

const CtaLink = styled('button', {
  shouldForwardProp: (prop) => prop !== 'accent',
})(({ accent }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 15,
  fontWeight: 600,
  color: T.textPrimary,
  background: 'transparent',
  textDecoration: 'none',
  padding: '4px 0',
  border: 'none',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  alignSelf: 'flex-start',
  fontFamily: fInter,
  '& .arrow': { transition: 'transform 0.2s ease', opacity: 0.8 },
  '&:hover': {
    color: accent,
    borderBottomColor: accent,
    '& .arrow': { transform: 'translateX(3px)', opacity: 1 },
  },
}));

/* ---- Meta ---- */
const ColMeta = styled(Box)({
  marginTop: 40,
  paddingTop: 32,
  borderTop: `1px solid ${T.border}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  fontFamily: fMono,
  fontSize: 11,
  letterSpacing: '0.08em',
});

const MetaLabel = styled('span')({
  color: T.textMuted,
  textTransform: 'uppercase',
});

const MetaValue = styled('span')({
  color: T.textSecondary,
  textTransform: 'uppercase',
  fontWeight: 500,
});

const AccentDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'accent',
})(({ accent }) => ({
  display: 'inline-block',
  width: 5,
  height: 5,
  borderRadius: '50%',
  marginRight: 6,
  verticalAlign: 'middle',
  background: accent,
  boxShadow: `0 0 8px ${accent}`,
}));

/* =====================================================================
   Visual: Resonance — pulse grid
   ===================================================================== */
const ResonanceVisual = () => (
  <Visual sx={{ background: 'linear-gradient(180deg, rgba(249, 115, 22, 0.05) 0%, transparent 100%)' }}>
    <Box component="svg" viewBox="0 0 400 220" preserveAspectRatio="xMidYMid slice"
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <defs>
        <radialGradient id="r-node-glow">
          <stop offset="0%" stopColor="#F97316" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g fill="#F97316" opacity="0.14">
        {[40, 80, 120, 160].flatMap((cy) =>
          [40, 90, 140, 190, 240, 290, 340].map((cx) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.5" />
          )),
        )}
      </g>
      <path d="M 90 120 L 190 80 L 290 160 L 340 80" stroke="#F97316" strokeWidth="1" fill="none" opacity="0.4" />
      <path d="M 40 80 L 140 160 L 240 120" stroke="#F97316" strokeWidth="1" fill="none" opacity="0.28" />

      {[[90, 120], [190, 80], [290, 160]].map(([cx, cy]) => (
        <g key={`n-${cx}-${cy}`}>
          <circle cx={cx} cy={cy} r="18" fill="url(#r-node-glow)" />
          <circle cx={cx} cy={cy} r="4" fill="#F97316" />
          <circle cx={cx} cy={cy} r="8" fill="none" stroke="#F97316" strokeWidth="1" opacity="0.5" />
        </g>
      ))}
      <circle cx="340" cy="80" r="3" fill="#F97316" opacity="0.7" />
    </Box>
  </Visual>
);

/* =====================================================================
   Visual: Research — biochar particles
   ===================================================================== */
const ResearchVisual = () => (
  <Visual
    sx={{
      background: '#1a1a1a',
      backgroundImage: 'radial-gradient(ellipse at center, rgba(40, 40, 40, 0.3) 0%, rgba(10, 10, 10, 0.9) 80%)',
    }}
  >
    <Box component="svg" viewBox="0 0 400 220" preserveAspectRatio="xMidYMid slice"
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <g fill="#000" opacity="0.85">
        <path d="M 40 80 L 55 75 L 68 88 L 62 100 L 48 98 Z" />
        <path d="M 95 120 L 115 115 L 128 130 L 118 145 L 100 142 Z" />
        <path d="M 165 60 L 185 55 L 200 70 L 192 85 L 172 82 Z" />
        <path d="M 220 150 L 245 148 L 258 165 L 245 180 L 225 175 Z" />
        <path d="M 290 90 L 312 85 L 325 100 L 318 115 L 298 112 Z" />
        <path d="M 340 170 L 360 165 L 372 180 L 362 195 L 345 192 Z" />
        <path d="M 20 180 L 38 175 L 50 188 L 42 200 L 25 198 Z" />
        <path d="M 130 30 L 148 28 L 160 42 L 152 55 L 135 52 Z" />
        <path d="M 260 25 L 278 22 L 290 35 L 282 48 L 265 45 Z" />
      </g>
      <g fill="#000" opacity="0.65">
        <rect x="70" y="40" width="18" height="6" rx="1" transform="rotate(25 79 43)" />
        <rect x="180" y="110" width="22" height="5" rx="1" transform="rotate(-15 191 112)" />
        <rect x="260" y="130" width="16" height="7" rx="1" transform="rotate(40 268 133)" />
        <rect x="110" y="180" width="20" height="5" rx="1" transform="rotate(-30 120 182)" />
        <rect x="310" y="50" width="14" height="6" rx="1" transform="rotate(15 317 53)" />
        <rect x="350" y="120" width="18" height="5" rx="1" transform="rotate(-45 359 122)" />
      </g>
      <g fill="#2a2a2a" opacity="0.7">
        <circle cx="80" cy="155" r="3" />
        <circle cx="155" cy="100" r="2.5" />
        <circle cx="230" cy="50" r="3" />
        <circle cx="305" cy="175" r="2.5" />
        <circle cx="375" cy="90" r="3" />
      </g>
      <g stroke="#444" strokeWidth="0.5" fill="none" opacity="0.3">
        <path d="M 40 80 L 55 75 L 68 88" />
        <path d="M 165 60 L 185 55 L 200 70" />
        <path d="M 290 90 L 312 85" />
      </g>
    </Box>
  </Visual>
);

/* =====================================================================
   Visual: Advisory — intersecting arcs
   ===================================================================== */
const AdvisoryVisual = () => (
  <Visual sx={{ background: 'linear-gradient(180deg, rgba(167, 139, 250, 0.04) 0%, transparent 100%)' }}>
    <Box component="svg" viewBox="0 0 400 220" preserveAspectRatio="xMidYMid slice"
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <defs>
        <radialGradient id="a-node-glow">
          <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#A78BFA" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="140" cy="110" r="75" fill="none" stroke="#A78BFA" strokeWidth="1" opacity="0.35" />
      <circle cx="200" cy="110" r="75" fill="none" stroke="#A78BFA" strokeWidth="1.3" opacity="0.55" />
      <circle cx="260" cy="110" r="75" fill="none" stroke="#A78BFA" strokeWidth="1" opacity="0.35" />
      <circle cx="200" cy="110" r="95" fill="none" stroke="#A78BFA" strokeWidth="0.5" opacity="0.2" strokeDasharray="2 4" />
      <circle cx="140" cy="110" r="3" fill="#A78BFA" opacity="0.7" />
      <circle cx="260" cy="110" r="3" fill="#A78BFA" opacity="0.7" />
      <circle cx="200" cy="110" r="22" fill="url(#a-node-glow)" />
      <circle cx="200" cy="110" r="5" fill="#A78BFA" />
      <circle cx="200" cy="110" r="10" fill="none" stroke="#A78BFA" strokeWidth="1" opacity="0.5" />
      <line x1="140" y1="110" x2="200" y2="110" stroke="#A78BFA" strokeWidth="0.8" opacity="0.45" strokeDasharray="2 3" />
      <line x1="200" y1="110" x2="260" y2="110" stroke="#A78BFA" strokeWidth="0.8" opacity="0.45" strokeDasharray="2 3" />
    </Box>
  </Visual>
);

/* =====================================================================
   Component
   ===================================================================== */
const ThreePillarsSection = ({ scrollToSection }) => {
  const { t } = useTranslation();

  return (
    <Section id="pillars-section">
      <ScrollArrow aria-hidden>↓</ScrollArrow>

      <Inner>
        <SectHeader>
          <SectEyebrow>{t('landing.pillars.sectionEyebrow')}</SectEyebrow>
          <SectTitle variant="h2">
            {t('landing.pillars.sectionTitlePre')}
            <br />
            <em>{t('landing.pillars.sectionTitleEmphasis')}</em>
          </SectTitle>
        </SectHeader>

        <Cols>
          {/* RESONANCE */}
          <Col>
            <ResonanceVisual />
            <ColBodyWrap>
              <ColEyebrow sx={{ color: T.orange }}>
                {t('landing.pillars.resonance.overline')}
              </ColEyebrow>
              <ColHeadline>{t('landing.pillars.resonance.title')}</ColHeadline>
              <ColBody>{t('landing.pillars.resonance.body')}</ColBody>
              <CtaButton
                href={RESONANCE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('landing.pillars.resonance.cta')}
                <span className="arrow">→</span>
              </CtaButton>
            </ColBodyWrap>
            <ColMeta>
              <MetaLabel>{t('landing.pillars.resonance.metaLabel')}</MetaLabel>
              <MetaValue>
                <AccentDot accent={T.orange} />
                {t('landing.pillars.resonance.metaValue')}
              </MetaValue>
            </ColMeta>
          </Col>

          {/* RESEARCH */}
          <Col>
            <ResearchVisual />
            <ColBodyWrap>
              <ColEyebrow sx={{ color: T.teal }}>
                {t('landing.pillars.biochar.overline')}
              </ColEyebrow>
              <ColHeadline>{t('landing.pillars.biochar.title')}</ColHeadline>
              <ColBody>{t('landing.pillars.biochar.body')}</ColBody>
              <CtaLink accent={T.teal} onClick={() => scrollToSection('biochar')}>
                {t('landing.pillars.biochar.cta')}
                <span className="arrow">↓</span>
              </CtaLink>
            </ColBodyWrap>
            <ColMeta>
              <MetaLabel>{t('landing.pillars.biochar.metaLabel')}</MetaLabel>
              <MetaValue>
                <AccentDot accent={T.teal} />
                {t('landing.pillars.biochar.metaValue')}
              </MetaValue>
            </ColMeta>
          </Col>

          {/* ADVISORY */}
          <Col>
            <AdvisoryVisual />
            <ColBodyWrap>
              <ColEyebrow sx={{ color: T.purple }}>
                {t('landing.pillars.advisory.overline')}
              </ColEyebrow>
              <ColHeadline>{t('landing.pillars.advisory.title')}</ColHeadline>
              <ColBody>{t('landing.pillars.advisory.body')}</ColBody>
              <CtaLink accent={T.purple} onClick={() => scrollToSection('advisory')}>
                {t('landing.pillars.advisory.cta')}
                <span className="arrow">→</span>
              </CtaLink>
            </ColBodyWrap>
            <ColMeta>
              <MetaLabel>{t('landing.pillars.advisory.metaLabel')}</MetaLabel>
              <MetaValue>
                <AccentDot accent={T.purple} />
                {t('landing.pillars.advisory.metaValue')}
              </MetaValue>
            </ColMeta>
          </Col>
        </Cols>
      </Inner>
    </Section>
  );
};

ThreePillarsSection.propTypes = {
  scrollToSection: PropTypes.func.isRequired,
};

export default ThreePillarsSection;
